import * as React from "react";
import { Link } from "react-router-dom";
import { FieldNav } from "../components/FieldNav.js";
import { FieldFooter } from "../components/FieldFooter.js";
import { MailtoContact } from "../components/MailtoContact.js";
import { useDocumentTitle } from "../hooks/useDocumentTitle.js";
import { SEO_TITLES } from "../lib/seo-titles.js";
import "../styles/landing-field.css";
import "../styles/content-pages-field.css";

/**
 * /trust — Trust & Safety. A dedicated, indexable page that states — in plain
 * language — the privacy/safety promises already true of the product (drawn
 * from PrivacyRoute + AboutRoute). It claims NO third-party certification we
 * don't hold (no Common Sense / Digital Promise / kidSAFE badges); every line
 * here is a commitment we already make. Built on the shared content-page shell
 * so it needs no bespoke CSS.
 */
export function TrustRoute(): React.ReactElement {
  useDocumentTitle(SEO_TITLES["/trust"]);
  return (
    <div className="v-field">
      <FieldNav />
      <main id="main" className="v-field-c__page">
        <div className="container">
          <span className="v-field-c__eyebrow">Trust &amp; Safety</span>
          <h1 className="v-field-c__title">
            Safe by <span className="stripe">design.</span> Private by default.
          </h1>
          <p className="v-field-c__lede">
            Sports Academy Games is built for classrooms and families. Every game is walled off from
            the rest of the web, and student data is never sold or used to advertise. Here is
            exactly what that means.
          </p>
          <div className="v-field-c__prose">
            <h2>Our promises</h2>
            <ul>
              <li>
                <strong>No ads inside the games.</strong> Every game runs walled off from the rest
                of the site — no ad networks, no ad SDKs, nothing selling attention while a student
                plays.
              </li>
              <li>
                <strong>No trackers watching students play.</strong> The game sandbox is
                tracker-free; nothing follows a student around the web.
              </li>
              <li>
                <strong>We never sell student data.</strong> We do not sell, rent, or share student
                data, and we never use it to advertise to students.
              </li>
              <li>
                <strong>Built for under-13 with consent.</strong> Children under 13 use Sports
                Academy Games with consent from a parent, guardian, or school — aligned with COPPA.
              </li>
              <li>
                <strong>Only the data we need.</strong> We collect the least we need to run the
                games and save progress, and nothing more.
              </li>
              <li>
                <strong>Works everywhere, installs nowhere.</strong> It runs in any modern browser
                on Chromebooks, iPads, phones, and laptops — no app install, no district approval
                required.
              </li>
            </ul>
            <h2>The details</h2>
            <p>
              Our full <Link to="/privacy">Privacy Policy</Link> spells out what we collect and why,
              and the <Link to="/terms">Terms of Use</Link> cover acceptable use. Have a question
              about student privacy or classroom use? A real person on our US team will answer.
            </p>
          </div>
          <div className="v-field-c__actions">
            <Link className="btn-field-primary" to="/games">
              Browse the games
            </Link>
            <MailtoContact className="btn-field-secondary">Email us</MailtoContact>
          </div>
        </div>
      </main>
      <FieldFooter />
    </div>
  );
}
